import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, mergeMap } from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { MassiveEditQualitativeActions } from './massive-edit-qualitative.actions';
import { QualitativeInputTreeService } from '../../service/qualitative-input-tree.service';
import { UpdateQualitativeInputTreeMutation } from '../../../../graphql/generated';
import { RiskMapTreeService } from '../../service/riskmaptree.service';
import { MutationData } from '../../shared/graphql-mutation-response.util';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';


@Injectable()
export class MassiveEditQualitativeEffects {

  loadQualitativeInputs$ = createEffect(
    (rs: QualitativeInputTreeService = inject(QualitativeInputTreeService)) => {
      return this.actions$.pipe(

        ofType(MassiveEditQualitativeActions.loadQualitativeInputs),
        mergeMap(({ campaignId, legalEntityId, completed, params, fields, au }) =>
          rs.getQualitative(campaignId, legalEntityId, completed as boolean, params, au).pipe(
            map((riskMap) => {
              return MassiveEditQualitativeActions.loadQualitativeInputsSuccess(
                {
                  qualitative: riskMap,
                  fields
                }
              );
            }),
            catchError((error) => of(MassiveEditQualitativeActions.loadQualitativeInputsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllQualitativeInput$ = createEffect(
    (rs: QualitativeInputTreeService = inject(QualitativeInputTreeService)) => {
      return this.actions$.pipe(
        ofType(MassiveEditQualitativeActions.loadAllQualitativeInputs),
        mergeMap(({ campaignId, legalEntityId, fields, au, completed, params }) =>
          rs.getAllQualitative(campaignId, legalEntityId, au).pipe(
            mergeMap((qualitative) => {
              const successAction = MassiveEditQualitativeActions.loadAllQualitativeInputsSuccess({
                qualitative,
                fields
              });

              // const anotherAction = MassiveEditQualitativeActions.loadQualitativeInputs({
              //   au,
              //   campaignId,
              //   legalEntityId,
              //   params,
              //   fields,
              //   completed
              // });

              return [successAction];
            }),
            catchError((error) =>
              of(MassiveEditQualitativeActions.loadAllQualitativeInputsFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateQualitativeInputs$ = createEffect(
    (
      rs: QualitativeInputTreeService = inject(QualitativeInputTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(MassiveEditQualitativeActions.updateQualitativeInputs),
        mergeMap(({ comments, id, opinionId, questionId, filters }) =>
          rs.updateQualitativeInput(id, questionId, opinionId, comments, filters).pipe(
            map((riskMap) => {
              return MassiveEditQualitativeActions.updateQualitativeInputsSuccess(
                {
                  qualitative: riskMap as MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>
                }
              );
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Qualitative Input Error',
                detail: graphQlErrorMessage(error, 'Unable to update the qualitative input'),
                life: 10000
              });
              return of(MassiveEditQualitativeActions.updateQualitativeInputsFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateQualitativeInputsMultiple$ = createEffect(
    (
      rs: QualitativeInputTreeService = inject(QualitativeInputTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(MassiveEditQualitativeActions.updateQualitativeInputsMultiple),
        mergeMap(({ changes, filters }) =>
          rs.updateQualitativeInputMultiple(changes, filters).pipe(
            map((riskMap) => {
              return MassiveEditQualitativeActions.updateQualitativeInputsMultipleSuccess(
                {
                  qualitative: riskMap as MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>
                }
              );
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Qualitative Input Error',
                detail: graphQlErrorMessage(error, 'Unable to update the qualitative input'),
                life: 10000
              });
              return of(MassiveEditQualitativeActions.updateQualitativeInputsMultipleFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateTreeAu$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(MassiveEditQualitativeActions.updateTreeAu),
        mergeMap(({ campaignId, legalEntityId, processId, cptIds, parameterName, filter }) =>
          rs.updateTreeAu(campaignId, legalEntityId, processId, filter).pipe(
            map((riskMap) => {
              return MassiveEditQualitativeActions.updateTreeAuSuccess({
                riskMap: riskMap,
                cptIds,
                parameterName
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Auditable Universe Error',
                detail: graphQlErrorMessage(error, 'Unable to update the auditable universe'),
                life: 10000
              });
              return of(MassiveEditQualitativeActions.updateTreeAuFailure({ error, cptIds, parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateTreeNote$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(MassiveEditQualitativeActions.updateTreeNote),
        mergeMap(({ campaignId, legalEntityId, processCode, cptIds, parameterName, note, filter }) =>
          rs.updateTreeNote(campaignId, legalEntityId, processCode, note, filter).pipe(
            map((riskMap) => {
              return MassiveEditQualitativeActions.updateTreeNoteSuccess({
                riskMap: riskMap,
                cptIds,
                parameterName,
                note
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Note Error',
                detail: graphQlErrorMessage(error, 'Unable to update the note'),
                life: 10000
              });
              return of(MassiveEditQualitativeActions.updateTreeNoteFailure({ error, cptIds, parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );


  downloadQualitativeInputTree$ = createEffect(
    (rs: QualitativeInputTreeService = inject(QualitativeInputTreeService)) => {
      return this.actions$.pipe(

        ofType(MassiveEditQualitativeActions.downloadQualitativeInput),
        mergeMap(({ campaignId, completed, legalEntityId, parameters, timeZone }) =>
          rs.downloadQualitativeInputTree(campaignId, completed, legalEntityId, parameters, timeZone).pipe(
            map((res) => {
              this.downloadFile(res!.data![0]!.content, res!.data![0]!.filename)
              return MassiveEditQualitativeActions.downloadQualitativeInputSuccess({
                res
              });
            }),
            catchError((error) => of(MassiveEditQualitativeActions.downloadQualitativeInputFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }
  constructor(private actions$: Actions) { }
}